import React from 'react';
import { observer } from 'mobx-react-lite';
import styled from 'styled-components';
import { EditorDemoStore } from '../EditorDemoStore';

interface PlaybackControlsProps {
  demoStore: EditorDemoStore;
  duration: number;
}

export const PlaybackControls = observer(({ demoStore, duration }: PlaybackControlsProps) => {
  // Format time as MM:SS.ms
  const formatTime = (seconds: number): string => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const ms = Math.floor((seconds % 1) * 10);
    return `${mins}:${secs.toString().padStart(2, '0')}.${ms}`;
  };
  
  // Handle scrubber change
  const handleScrubberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const time = parseFloat(e.target.value);
    demoStore.setTime(time, true);
  };
  
  // Handle keyboard shortcuts
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === ' ') {
      e.preventDefault();
      demoStore.togglePlayback();
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      demoStore.setTime(demoStore.currentTime - 1, true);
    } else if (e.key === 'ArrowRight') {
      e.preventDefault();
      demoStore.setTime(demoStore.currentTime + 1, true);
    }
  };
  
  return (
    <ControlsContainer tabIndex={0} onKeyDown={handleKeyDown}>
      <ControlsTop>
        <TimeDisplay>
          {formatTime(demoStore.currentTime)} / {formatTime(duration)}
        </TimeDisplay>
        
        <ButtonGroup>
          <ControlButton onClick={() => demoStore.setTime(0, true)}>⏮</ControlButton>
          <ControlButton onClick={() => demoStore.setTime(Math.max(0, demoStore.currentTime - 1), true)}>⏪</ControlButton>
          <PlayButton onClick={() => demoStore.togglePlayback()}>
            {demoStore.isPlaying ? '⏸' : '▶️'}
          </PlayButton>
          <ControlButton onClick={() => demoStore.setTime(Math.min(duration, demoStore.currentTime + 1), true)}>⏩</ControlButton>
          <ControlButton onClick={() => demoStore.setTime(duration, true)}>⏭</ControlButton>
        </ButtonGroup>
      </ControlsTop>
      
      <ScrubberContainer>
        <Scrubber 
          type="range"
          min={0}
          max={duration || 10}
          step={0.1}
          value={demoStore.currentTime}
          onChange={handleScrubberChange}
        />
      </ScrubberContainer>
    </ControlsContainer>
  );
});

const ControlsContainer = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px;
  background-color: #171724;
  border-radius: 4px;
  margin-bottom: 16px;
  
  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.3);
  }
`;

const ControlsTop = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
`;

const TimeDisplay = styled.div`
  color: #FFF;
  font-size: 14px;
  font-family: monospace;
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`;

const ControlButton = styled.button`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 32px;
  height: 32px;
  border: none;
  border-radius: 4px;
  background-color: #333;
  color: #FFF;
  font-size: 14px;
  cursor: pointer;
  
  &:hover {
    background-color: #444;
  }
`;

const PlayButton = styled(ControlButton)`
  width: 48px;
  background-color: #006FEE;
  
  &:hover {
    background-color: #0060D9;
  }
`;

const ScrubberContainer = styled.div`
  width: 100%;
`;

const Scrubber = styled.input`
  width: 100%;
  height: 8px;
  -webkit-appearance: none;
  background: #333;
  outline: none;
  border-radius: 4px;
  
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #FF3D71;
    cursor: pointer;
  }
  
  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    border-radius: 50%;
    background: #FF3D71;
    cursor: pointer;
    border: none;
  }
`;